import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * ProgressArc — otwarty łuk postępu (240-300°), zgodny z sekcją 8.2
 * .windsurf/docs/Engagement_Challenges_UI_Design_Direction_v1.md.
 * Zastępuje generyczny pełny "progress ring".
 */
function ProgressArc({ value = 0, size = 96, label, sublabel, ariaLabel }) {
  const theme = useTheme();
  const tokens = theme.tokens?.components?.progressArc;
  const stroke = tokens?.strokeWidth ?? 10;
  const trackColor = tokens?.track ?? 'rgba(93,159,32,0.14)';
  const fillColor = tokens?.fill ?? '#9DE94B';

  const radius = (size - stroke) / 2;
  const startAngle = -210; // otwarty łuk ~300 stopni
  const sweepAngle = 300;
  const circumference = 2 * Math.PI * radius * (sweepAngle / 360);
  const clamped = Math.min(Math.max(value, 0), 100);
  const dashOffset = circumference * (1 - clamped / 100);

  const center = size / 2;

  return (
    <Box
      role="img"
      aria-label={ariaLabel || `Postęp ${clamped}%`}
      sx={{ position: 'relative', width: size, height: size, display: 'inline-flex' }}
    >
      <svg width={size} height={size} style={{ transform: `rotate(${startAngle}deg)` }}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={stroke}
          strokeDasharray={`${circumference} ${2 * Math.PI * radius}`}
          strokeLinecap="round"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={fillColor}
          strokeWidth={stroke}
          strokeDasharray={`${circumference} ${2 * Math.PI * radius}`}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transition: `stroke-dashoffset ${theme.transitions.duration.complex}ms ${theme.transitions.easing.easeOut}` }}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {label && (
          <Typography variant="body1" fontWeight={700} sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}>
            {label}
          </Typography>
        )}
        {sublabel && (
          <Typography variant="caption" color="text.secondary">
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default ProgressArc;
